import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, useWatch, type Path, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDate } from '../../utils/formatDate';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
    mdiBroom,
    mdiBrush,
    mdiCamera,
    mdiExcavator,
    mdiMapSearch,
    mdiPencil,
    mdiShovel,
    mdiWheelBarrow
} from '@mdi/js';
import MdiIcon from '../../components/MdiIcon';
import dayjs from 'dayjs';
import { v4 as uuid } from 'uuid';
import { useRxCollection, useLiveRxQuery } from 'rxdb/plugins/react';
import {
    diaryInputSchema,
    requiredBreakMinutes,
    WEATHER_CONDITION_OPTIONS,
    type DiaryDocType,
    type DiaryVisitor,
    type DiarySondagePhoto,
    type ParcelDocType
} from '@ast-data/shared';
import { latestBookableTime } from '../timeTracking/timeMath';
import { useValidationMessage } from '../../i18n/validationMessages';
import { useSavedToast, useToast } from '../../components/toastContext';
import { usePersonPicker } from '../../components/personPicker';
import { useEmploymentDay } from '../users/employment';
import {
    storedPersonLinkList,
    useCurrentPerson,
    usePersonUserId
} from '../users/personUserId';
import { useSelfPrefill } from '../users/useSelfPrefill';
import RecordedAtField from '../../components/RecordedAtField';
import TimeStringField from '../../components/TimeStringField';
import FormRow from '../../components/FormRow';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import RecordFormDialog from '../../components/RecordFormDialog';
import FormSectionBox from '../../components/FormSectionBox';
import { SectionNavMenuButton } from '../../components/formSectionNav';
import { type FormSection as FormSectionNavEntry } from '../../components/formSectionNavContext';
import {
    bindFormFields,
    labelIdsFrom,
    requiredInForm
} from '../../components/formFields';
import type { RichTextFieldOptions } from '../../components/formFields/richTextField';
import { useFindingRefSupport } from './useFindingRefSupport';
import { useUnsavedCloseGuard } from '../../components/useUnsavedCloseGuard';
import { useGuardedNavigate } from '../../components/useGuardedNavigate';
import { useRecordSiblings } from '../../components/useRecordSiblings';
import { recordSortModel } from '../../components/useRecordGrid';
import { DIARY_GRID } from './recordGrids';
import { useForwardBackState } from '../../components/dialogBackTrail';
import { useFocusFieldOnOpen } from '../../components/cellFocus';
import { Link as RouterLink } from 'react-router-dom';
import { linkClick } from '../../components/routerLink';
import { useDiaryCrew, splitPeople } from './useDiaryCrew';
import { parcelPickerLabel } from './parcelPickerLabel';
import { renderCheckableOption } from '../../components/checkableOption';
import { chipClipboardProps } from '../../components/chipClipboard';
import {
    breakToMinutes,
    distinct,
    embeddedKey,
    emptyValues,
    toValues,
    type FormValues
} from './diaryFormValues';
import { timeToMinutes } from '../timeTracking/timeMath';
import { useDiaryDay } from './useDiaryDay';
import { findDiaryOnDay } from './diaryDuplicates';
import {
    carryOverNumbers,
    diaryNumbersWentBackwards,
    findPreviousDiaryDay,
    DIARY_FORM_NUMBER_FIELDS,
    type DiaryFormNumberField
} from './diaryNumbers';
import {
    diaryCarryOverOffers,
    openCarryOverOffers,
    type DiaryCarryOverOffer
} from './diaryCarryOver';
import DiaryPreviousNumbersList from './DiaryPreviousNumbersList';
import {
    useDiaryNumberLinks,
    type RecordNumberTarget
} from './useDiaryNumberLinks';
import DiaryDayPeopleList from './DiaryDayPeopleList';
import DiaryVisitorsEditor from './DiaryVisitorsEditor';
import { namelessVisitors, type VisitorRow } from './visitorRows';
import DiarySondagePhotoEditor from './DiarySondagePhotoEditor';
import {
    filledSondagePhotos,
    lastPartialPhotoNumber,
    sondagePhotosWentBackwards,
    type SondagePhotoRow
} from './sondagePhotoRows';
import DiaryWorkSpanHint from './DiaryWorkSpanHint';
import DiaryDayRecordsList from './DiaryDayRecordsList';
import { MediaSectionBox } from './descriptionSections';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useRecordTranslations } from '../translation/recordTranslations';
import { useDiaryPdfExport } from '../export/diaryPdf/useDiaryPdfExport';
import { theseRecords } from '../export/pdf/exportScope';

import { compareNatural } from '../../utils/compare';

// Grid columns whose `field` differs from the Tagebuch form input to focus. The
// `weather` column stands for several inputs — focus the condition.
const DIARY_CELL_FOCUS_ALIASES: Record<string, string> = {
    weather: 'weatherCondition'
};

// Ordered sections for the in-form nav; must mirror the FormSection anchorIds.
const FORM_SECTIONS: FormSectionNavEntry[] = [
    { id: 'identity', titleId: 'diary.section.identity' },
    { id: 'crew', titleId: 'diary.section.crew' },
    // Besuch follows the crew: both answer "who was on site that day".
    { id: 'visitors', titleId: 'diary.section.visitors' },
    { id: 'time', titleId: 'diary.section.time' },
    { id: 'weather', titleId: 'diary.section.weather' },
    { id: 'activities', titleId: 'diary.section.activities' },
    { id: 'excavator', titleId: 'diary.section.excavator' },
    { id: 'numbers', titleId: 'diary.section.numbers' },
    { id: 'records', titleId: 'diary.section.records' },
    { id: 'media', titleId: 'media.title' }
];

interface DiaryFormDialogProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    diary?: DiaryDocType | null;
    /** Grid column the user double-clicked, to focus the matching input on open. */
    focusField?: string;
    /**
     * Route the Befunde of a project are mounted under — office and field mount
     * the same editors at different paths, so the host supplies it. Takes the
     * TARGET's project id: a family shares its numbers across measures, so the
     * Befund a Letzte-Nr. names may live in a sibling project. Omitted, the
     * Letzte-Nrn. carry no links.
     */
    findingsBasePath?: (projectId: string) => string;
    /**
     * Route to the Zeiterfassung, filtered to one project's day. Omitted, the
     * Arbeitszeit hint offers no way through.
     */
    timeEntriesPath?: (projectId: string, date: string) => string;
    /**
     * Where a project's grids live, e.g. `/office/projects/<id>`. Given, the day
     * gets a section listing what else was recorded on it, each kind linking to
     * its own grid filtered to that day. Omitted (the field app), it is left out.
     */
    projectBasePath?: (projectId: string) => string;
}

/**
 * The Tagebuch editor — a fullscreen dialog mirroring the other record forms.
 * People are Mitarbeiter selects (active users), the Arbeitszeit is validated
 * like the Zeiterfassung, and the day's date is the Aufnahmedatum.
 */
/**
 * The person the record is written up by is required in the form — see
 * requiredInForm.
 */
const DIARY_FORM_SCHEMA = requiredInForm(diaryInputSchema, ['author']);

export default function DiaryFormDialog({
    open,
    onClose,
    projectId,
    diary,
    focusField,
    findingsBasePath,
    timeEntriesPath,
    projectBasePath
}: DiaryFormDialogProps) {
    const intl = useIntl();
    const collection = useRxCollection<DiaryDocType>('diaries');
    const validationMessage = useValidationMessage();
    const notifySaved = useSavedToast();
    const toast = useToast();
    const isEdit = !!diary;
    // A reader opens the record to look at it: the dialog shows it as a
    // sheet and has nothing to save (see RecordFormDialog).
    const readOnly = !useAbility().can(
        isEdit ? 'update' : 'create',
        scopedSubject('Diary', { projectId })
    );
    const ability = useAbility();
    const canEditMedia = ability.can(
        'update',
        scopedSubject('Diary', { projectId })
    );

    // The project's parcels populate the multi-select; existing diaries backtrack
    // people suggestions and pre-fill the Grabungsleiter/Techniker.
    const parcelsQuery = useStableQuery<ParcelDocType>({
        selector: { projectId }
    });
    const { results: parcelsDocs } = useLiveRxQuery<ParcelDocType>({
        collection: 'parcels',
        query: parcelsQuery
    });
    const parcels = useMemo(
        () => parcelsDocs.map((doc) => doc.toMutableJSON() as ParcelDocType),
        [parcelsDocs]
    );
    const diariesQuery = useStableQuery<DiaryDocType>({
        selector: { projectId }
    });
    const { results: diariesDocs } = useLiveRxQuery<DiaryDocType>({
        collection: 'diaries',
        query: diariesQuery
    });
    const diaries = useMemo(
        () => diariesDocs.map((doc) => doc.toMutableJSON() as DiaryDocType),
        [diariesDocs]
    );

    const currentUserName = useCurrentPerson()?.name ?? '';
    // The links this record already carries, name by name: a name nobody
    // touched keeps its account even where the rule can no longer place it
    // (see usePersonUserId).
    const keptLinks = useMemo(
        () =>
            diary
                ? [
                      { name: diary.author, id: diary.authorId },
                      {
                          name: diary.excavationLeader,
                          id: diary.excavationLeaderId
                      },
                      ...storedPersonLinkList(
                          splitPeople(diary.technician),
                          diary.technicianIds
                      ),
                      ...storedPersonLinkList(
                          splitPeople(diary.staff),
                          diary.staffIds
                      )
                  ]
                : [],
        [diary]
    );
    const { resolve, resolveAll } = usePersonUserId(keptLinks);

    // The most recent other diary — its Grabungsleiter/Techniker pre-fill a new day.
    const previousDiary = useMemo(() => {
        const others = diaries.filter(
            (candidate) => candidate.id !== (diary?.id ?? '')
        );
        if (others.length === 0) {
            return null;
        }
        return [...others].sort((firstDiary, secondDiary) =>
            secondDiary.recordedAt.localeCompare(firstDiary.recordedAt)
        )[0];
    }, [diaries, diary]);

    const sortedParcels = useMemo(
        () =>
            [...parcels].sort((firstParcel, secondParcel) =>
                compareNatural(
                    parcelPickerLabel(firstParcel),
                    parcelPickerLabel(secondParcel)
                )
            ),
        [parcels]
    );

    // The Baggertyp is the last free-text field here that offers what the
    // project has typed before; the person fields are pickers with their own
    // list (see components/personPicker).
    const suggestions = useMemo(
        () => ({
            excavatorType: distinct(diaries.map((entry) => entry.excavatorType))
        }),
        [diaries]
    );

    // Parcel ids, visitors and Sondagen are embedded on the diary doc; the
    // editable copies give each row a local id for a stable list key.
    const linkedParcelIds = useMemo(() => diary?.parcelIds ?? [], [diary]);
    const existingVisitors = useMemo<VisitorRow[]>(
        () =>
            (diary?.visitors ?? []).map((visitor) => ({
                id: uuid(),
                name: visitor.name,
                position: visitor.position,
                note: visitor.note ?? ''
            })),
        [diary]
    );
    const existingSondagePhotos = useMemo<SondagePhotoRow[]>(
        () =>
            (diary?.sondagePhotos ?? []).map((entry) => ({
                id: uuid(),
                sondage: entry.sondage,
                partialPhoto: entry.partialPhoto
            })),
        [diary]
    );

    const [selectedParcelIds, setSelectedParcelIds] = useState<string[]>([]);
    const [visitors, setVisitors] = useState<VisitorRow[]>([]);
    const [sondagePhotos, setSondagePhotos] = useState<SondagePhotoRow[]>([]);

    const { exportPdf, busyId: pdfBusyId } = useDiaryPdfExport();
    const exportingPdf = pdfBusyId === projectId;

    const {
        register,
        handleSubmit,
        reset,
        setError,
        setFocus,
        setValue,
        getValues,
        control,
        formState: { errors, isSubmitting, isDirty }
    } = useForm<FormValues>({
        resolver: zodResolver(
            DIARY_FORM_SCHEMA
        ) as unknown as Resolver<FormValues>,
        defaultValues: emptyValues()
    });

    // Only who was employed on the Aufnahmedatum (see users/employment).
    const employmentDay = useEmploymentDay(control, 'recordedAt');
    const personPicker = usePersonPicker(employmentDay);

    const {
        text,
        suggest,
        person,
        personMultiJoined,
        multiSelectJoined,
        richText
    } = bindFormFields<FormValues>({
        intl,
        readOnly,
        register,
        control,
        errors,
        validationMessage,
        labelId: labelIdsFrom('diary.field'),
        // The temperature is stored as the bare reading ("+10"); the unit
        // belongs to the field, and every export puts it back on.
        unitId: (name) =>
            name === 'weatherTemperature' ? 'common.degreesCelsius' : undefined
    });

    // The account names can arrive after the dialog opened, and the reset
    // below then had nothing to prefill with (see useSelfPrefill).
    useSelfPrefill(open && !diary, currentUserName, (self) => {
        if (getValues('author').trim() === '') {
            setValue('author', self);
        }
    });

    // An effect EVENT, so every prefill source is read at its CURRENT value
    // while the reset itself runs only when the dialog (re)opens for a record —
    // a background sync must not re-reset the form and wipe what was typed.
    const resetForOpening = useEffectEvent(() => {
        const values = diary ? toValues(diary) : emptyValues();
        reset({
            ...values,
            // A new day defaults the Autor to whoever is recording and the
            // Grabungsleiter/Techniker to the previous day's.
            author: values.author || currentUserName,
            excavationLeader:
                values.excavationLeader ||
                previousDiary?.excavationLeader ||
                '',
            technician: values.technician || previousDiary?.technician || ''
        });
        setSelectedParcelIds(linkedParcelIds);
        setVisitors(existingVisitors);
        setSondagePhotos(existingSondagePhotos);
    });
    useEffect(() => {
        if (open) {
            // react-hook-form's `reset` publishes to its own subscribers, so it
            // cannot be moved into the render pass the way plain state can.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            resetForOpening();
        }
    }, [open, diary]);

    useFocusFieldOnOpen(setFocus, open, focusField, DIARY_CELL_FOCUS_ALIASES);

    const selectedParcels = useMemo(
        () =>
            sortedParcels.filter((parcel) =>
                selectedParcelIds.includes(parcel.id)
            ),
        [sortedParcels, selectedParcelIds]
    );

    const embeddedDirty = useMemo(
        () =>
            embeddedKey(selectedParcelIds, visitors, sondagePhotos) !==
            embeddedKey(
                linkedParcelIds,
                existingVisitors,
                existingSondagePhotos
            ),
        [
            selectedParcelIds,
            visitors,
            sondagePhotos,
            linkedParcelIds,
            existingVisitors,
            existingSondagePhotos
        ]
    );

    // Cap the work-time pickers at the bookable bound (now + buffer) when the
    // recorded day is today, mirroring the Zeiterfassung.
    const watchedRecordedAt = useWatch({ control, name: 'recordedAt' });
    const bookableUntil = latestBookableTime();
    const maxTime =
        watchedRecordedAt &&
        dayjs(watchedRecordedAt).isSame(dayjs(), 'day') &&
        bookableUntil
            ? dayjs(`2000-01-01T${bookableUntil}`)
            : undefined;

    // What the rest of the app already recorded for this day: the outer bounds
    // of the Zeiterfassung and everyone who worked on the project.
    const {
        workSpan,
        pauseMinutes: dayPauseMinutes,
        people: dayPeople,
        counts: dayCounts
    } = useDiaryDay(projectId, watchedRecordedAt);
    const dayIso = watchedRecordedAt
        ? dayjs(watchedRecordedAt).format('YYYY-MM-DD')
        : '';
    const [watchedLeader, watchedTechnician, watchedStaff] = useWatch({
        control,
        name: ['excavationLeader', 'technician', 'staff']
    });
    const { leadRoleByKey, staffKeys, missingStaff, addToStaff } = useDiaryCrew(
        {
            excavationLeader: watchedLeader,
            technician: watchedTechnician,
            staff: watchedStaff,
            storedLinks: keptLinks,
            dayPeople,
            getStaff: () => getValues('staff'),
            setStaff: (value) => setValue('staff', value, { shouldDirty: true })
        }
    );

    // Advisory § 4 ArbZG break warning for the day's single work span — same rule
    // the Zeiterfassung flags (non-blocking).
    const [watchedStart, watchedEnd, watchedBreak] = useWatch({
        control,
        name: ['workStart', 'workEnd', 'breakTime']
    });
    const breakWarningId = useMemo(() => {
        const start = timeToMinutes(watchedStart);
        const end = timeToMinutes(watchedEnd);
        if (start === null || end === null || end <= start) {
            return null;
        }
        const pause = breakToMinutes(watchedBreak);
        const required = requiredBreakMinutes(end - start - pause);
        if (required > 0 && pause < required) {
            return required >= 45
                ? 'timeEntry.warning.shortBreak45'
                : 'timeEntry.warning.shortBreak30';
        }
        return null;
    }, [watchedStart, watchedEnd, watchedBreak]);

    const translations = useRecordTranslations({
        ownerType: 'diary',
        ownerId: diary?.id,
        projectId,
        readOnly
    });
    const closeGuard = useUnsavedCloseGuard({
        open,
        isDirty: isDirty || embeddedDirty || translations.isDirty,
        onClose,
        onSave: () => handleSubmit(onSubmit)()
    });

    // The Befund/Profil a Letzte-Nr. names, as it stands in the FORM (which may
    // have been corrected by hand).
    const [watchedLastFinding, watchedLastProfile] = useWatch({
        control,
        name: ['lastFindingNumber', 'lastProfileNumber']
    });
    const numberLinks = useDiaryNumberLinks(
        projectId,
        watchedLastFinding,
        watchedLastProfile
    );

    // A number handed out so far can only grow, so a day naming a smaller one
    // than the day before it is almost always a typo. Advisory only — the crew
    // records what it wrote down, and the day saves either way.
    const previousDay = useMemo(
        () =>
            findPreviousDiaryDay(
                diaries,
                projectId,
                watchedRecordedAt,
                diary?.id ?? ''
            ),
        [diaries, projectId, watchedRecordedAt, diary]
    );
    const watchedNumbers = useWatch({
        control,
        name: DIARY_FORM_NUMBER_FIELDS
    });
    const formNumbers = Object.fromEntries(
        DIARY_FORM_NUMBER_FIELDS.map((field, index) => [
            field,
            watchedNumbers[index] ?? ''
        ])
    ) as Record<DiaryFormNumberField, string>;
    const numberWarnings = diaryNumbersWentBackwards(formNumbers, previousDay);
    // Sondage and Teilfoto are checked per SONDAGE instead: a day may reopen an
    // older Sondage, but the same one cannot be photographed later under a
    // lower number.
    const photoWarnings = sondagePhotosWentBackwards(
        sondagePhotos,
        diaries,
        projectId,
        watchedRecordedAt,
        diary?.id ?? ''
    );
    // Everything that day said, as ONE list of offers — the four numbers and
    // its Sondagen alike, each marked taken once this day says it too: from
    // where the user stands they answer the same question.
    const carryOverDate = previousDay ? formatDate(previousDay.recordedAt) : '';
    const carryOverOffers = diaryCarryOverOffers(
        carryOverNumbers(previousDay),
        previousDay?.sondagePhotos ?? [],
        formNumbers,
        sondagePhotos
    );
    const openOffers = openCarryOverOffers(carryOverOffers);

    function takeCarryOver(offer: DiaryCarryOverOffer) {
        if (offer.kind === 'number') {
            setValue(offer.field, offer.value, {
                shouldDirty: true,
                shouldValidate: true
            });
            return;
        }
        setSondagePhotos((rows) => [...rows, { ...offer.entry, id: uuid() }]);
    }

    /** The usual day: nothing new was handed out, so all of it carries over. */
    function takeAllCarryOver() {
        const rows: SondagePhotoRow[] = [];
        for (const offer of openOffers) {
            if (offer.kind === 'number') {
                setValue(offer.field, offer.value, {
                    shouldDirty: true,
                    shouldValidate: true
                });
            } else {
                rows.push({ ...offer.entry, id: uuid() });
            }
        }
        if (rows.length > 0) {
            setSondagePhotos((current) => [...current, ...rows]);
        }
    }
    const guardedNav = useGuardedNavigate({
        isDirty: isDirty || embeddedDirty || translations.isDirty,
        submit: () => handleSubmit(onSubmit)(),
        close: () => closeGuard.close(),
        forwardState: useForwardBackState(
            intl.formatMessage(
                { id: 'diary.form.editTitleDate' },
                { date: formatDate(diary?.recordedAt ?? '') }
            )
        )
    });

    // Step to the day before/after this one, in the grid's order — through the
    // nav guard, like any other link out of an open form.
    const siblings = useRecordSiblings<DiaryDocType>({
        collection: 'diaries',
        projectId,
        currentId: diary?.id ?? '',
        sortModel: recordSortModel(DIARY_GRID),
        label: (day) => formatDate(day.recordedAt),
        requestNavigate: guardedNav.requestNavigate
    });

    // Every prose field of a day, with the Befunde it names marked and linked —
    // the Tagebuch is where references are written by hand ("Bef. 7000
    // geschnitten"). Defined AFTER `guardedNav` on purpose: a chip is a link
    // out of an open editor, so it has to ask before leaving.
    const findingRefs = useFindingRefSupport(
        projectId,
        guardedNav.requestNavigate
    );
    const refText = (name: Path<FormValues>, options?: RichTextFieldOptions) =>
        richText(name, { ...options, findingRefs });

    /** Where a resolved Letzte-Nr. target opens (null without a base path). */
    function targetPath(target: RecordNumberTarget | null): string | null {
        if (!target || !findingsBasePath) {
            return null;
        }
        const base = `${findingsBasePath(target.projectId)}/${target.findingId}`;
        return target.profileId
            ? `${base}/profiles/${target.profileId}/edit`
            : `${base}/edit`;
    }

    async function onSubmit(data: FormValues) {
        if (readOnly) {
            return;
        }
        if (!collection) {
            return;
        }
        // One Tagebuch per day and project — the day IS the entry's identity
        // (the ExcaBook import matches on it too). Best-effort against what
        // this device holds; a day written twice offline is caught by the
        // grid's duplicate marker once both sync.
        if (
            findDiaryOnDay(diaries, projectId, data.recordedAt, diary?.id ?? '')
        ) {
            setError(
                'recordedAt',
                { type: 'validate', message: 'duplicateDate' },
                { shouldFocus: true }
            );
            return;
        }
        // A visit with a function or a note but no name identifies nobody. The
        // row is marked in the editor, but the Besuch section may be scrolled
        // out of sight — so say it here too, and save nothing.
        const nameless = namelessVisitors(visitors);
        if (nameless.length > 0) {
            toast.notify(
                intl.formatMessage(
                    { id: 'diary.visitor.nameRequired' },
                    { count: nameless.length }
                ),
                'error'
            );
            return;
        }
        const now = new Date().toISOString();
        const embedded: {
            visitors: DiaryVisitor[];
            parcelIds: string[];
            sondagePhotos: DiarySondagePhoto[];
            lastPartialPhotoNumber: string;
        } = {
            /**
             * The last Teilfoto is DERIVED from the day's Sondagen: the field
             * stays on the doc as the highest number of the day, which is what
             * the grid column reads. The last Sondage is typed on its own.
             */
            sondagePhotos: filledSondagePhotos(sondagePhotos),
            lastPartialPhotoNumber: lastPartialPhotoNumber(sondagePhotos),
            visitors: visitors
                .filter(
                    (row) =>
                        row.name.trim() ||
                        row.position.trim() ||
                        row.note.trim()
                )
                .map((row) => ({
                    name: row.name,
                    position: row.position,
                    note: row.note
                })),
            parcelIds: selectedParcelIds
        };

        // The multi fields' id arrays are POSITIONAL to their ", "-split names
        // (the crew splits them the same way).
        const personLinks = {
            authorId: resolve(data.author),
            excavationLeaderId: resolve(data.excavationLeader),
            technicianIds: resolveAll(splitPeople(data.technician)),
            staffIds: resolveAll(splitPeople(data.staff))
        };

        const savedId = isEdit ? diary!.id : uuid();
        if (isEdit) {
            const doc = await collection.findOne(savedId).exec();
            if (doc) {
                await doc.incrementalPatch({
                    ...data,
                    ...embedded,
                    ...personLinks,
                    updatedAt: now
                });
            }
        } else {
            await collection.insert({
                id: savedId,
                projectId,
                ...data,
                ...embedded,
                ...personLinks,
                createdBy: '',
                updatedBy: '',
                updatedAt: now,
                createdAt: now
            });
        }
        await translations.commit(savedId);
        notifySaved();
        // Through the navigation guard: a routed dialog must step over the
        // guard's history sentinel, and a "save and go" continues to the
        // Befund/Profil instead of just closing.
        guardedNav.finishSubmit();
    }

    // One of the "Letzte vergebene Nrn.". Where the number names a record we
    // hold, the field carries a button through to it — the number alone leaves
    // you searching for the Befund it refers to.
    function numberField(
        name: DiaryFormNumberField,
        target: RecordNumberTarget | null = null
    ) {
        // The carry-over panel fills these by button, so the label has to be
        // told when that happened (see TextFieldOptions.shrinkLabel).
        const shrinkLabel = formNumbers[name] !== '';
        const path = targetPath(target);
        if (!path || !target) {
            return text(name, { shrinkLabel });
        }
        const open = intl.formatMessage(
            { id: 'diary.numbers.open' },
            { label: target.label }
        );
        return text(name, {
            shrinkLabel,
            endAdornment: (
                <InputAdornment position="end">
                    <Tooltip title={open}>
                        <IconButton
                            size="small"
                            edge="end"
                            aria-label={open}
                            component={RouterLink}
                            to={path}
                            onClick={linkClick(() =>
                                guardedNav.requestNavigate(path)
                            )}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>
            )
        });
    }

    function time(name: keyof FormValues, capToNow = false) {
        return (
            <TimeStringField
                control={control}
                name={name}
                label={intl.formatMessage({ id: `diary.field.${name}` })}
                maxTime={capToNow ? maxTime : undefined}
            />
        );
    }

    // Parked away from Speichern/Abbrechen — not one of the dialog's own
    // actions. Only for a SAVED day: the export reads the stored record.
    const pdfButton = isEdit && diary && (
        <Button
            variant="outlined"
            color="inherit"
            startIcon={
                exportingPdf ? (
                    <CircularProgress size={18} color="inherit" />
                ) : (
                    <PictureAsPdfIcon />
                )
            }
            disabled={exportingPdf}
            onClick={() => void exportPdf(theseRecords([diary.id]), projectId)}
            sx={{ mr: 'auto' }}
        >
            <FormattedMessage id="diaryPdf.export.one" />
        </Button>
    );

    return (
        <RecordFormDialog
            open={open}
            sections={FORM_SECTIONS}
            onSubmit={handleSubmit(onSubmit)}
            closeGuard={closeGuard}
            navGuard={guardedNav}
            isSubmitting={isSubmitting}
            readOnly={readOnly}
            translations={translations}
            footerStartSlot={pdfButton}
            appBar={
                <FormDialogAppBar
                    projectId={projectId}
                    onClose={closeGuard.requestClose}
                    startSlot={<SectionNavMenuButton />}
                    siblings={siblings}
                    title={
                        // One entry per day, so the day is its identity.
                        isEdit && diary?.recordedAt ? (
                            <FormattedMessage
                                id="diary.form.editTitleDate"
                                values={{
                                    date: formatDate(diary.recordedAt)
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id={
                                    isEdit
                                        ? 'diary.form.editTitle'
                                        : 'diary.form.newTitle'
                                }
                            />
                        )
                    }
                />
            }
        >
            <FormSectionBox titlePrefix="diary.section" id="identity">
                <FormRow>
                    <RecordedAtField
                        control={control}
                        name="recordedAt"
                        disableFuture
                    />
                    {person('author', personPicker)}
                </FormRow>
                {/* The Flurstücke belong to the day itself, so they sit here
                    rather than in a block of their own. */}
                <Autocomplete
                    multiple
                    readOnly={readOnly}
                    disableCloseOnSelect
                    renderOption={renderCheckableOption({
                        ticks: true,
                        label: parcelPickerLabel
                    })}
                    options={sortedParcels}
                    value={selectedParcels}
                    onChange={(_event, value) =>
                        setSelectedParcelIds(value.map((parcel) => parcel.id))
                    }
                    {...chipClipboardProps({
                        value: selectedParcels,
                        getLabel: parcelPickerLabel,
                        options: sortedParcels,
                        onChange: (value) =>
                            setSelectedParcelIds(
                                value.map((parcel) => parcel.id)
                            ),
                        readOnly
                    })}
                    getOptionLabel={parcelPickerLabel}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={intl.formatMessage({
                                id: 'diary.field.parcels'
                            })}
                            placeholder={intl.formatMessage({
                                id: 'diary.field.parcelsPlaceholder'
                            })}
                        />
                    )}
                />
                {refText('dailyCourse')}
            </FormSectionBox>

            <FormSectionBox titlePrefix="diary.section" id="crew">
                <FormRow>
                    {person('excavationLeader', personPicker, {
                        leads: true
                    })}
                    {personMultiJoined('technician', personPicker)}
                </FormRow>
                {personMultiJoined('staff', personPicker)}
                {/* Everyone who recorded something for the project on this day
                    — one tap moves them into Personal. */}
                <DiaryDayPeopleList
                    projectId={projectId}
                    people={dayPeople}
                    staffKeys={staffKeys}
                    leadRoleByKey={leadRoleByKey}
                    missingCount={missingStaff.length}
                    onAdd={(name) => addToStaff([name])}
                    onAddAll={() =>
                        addToStaff(missingStaff.map((person) => person.name))
                    }
                />
            </FormSectionBox>

            <FormSectionBox titlePrefix="diary.section" id="visitors">
                <DiaryVisitorsEditor value={visitors} onChange={setVisitors} />
            </FormSectionBox>

            <FormSectionBox titlePrefix="diary.section" id="time">
                <FormRow>
                    {time('workStart', true)}
                    {time('workEnd', true)}
                    {/* "Als Pause übernehmen" fills this one by button too. */}
                    {text('breakTime', { shrinkLabel: !!watchedBreak })}
                </FormRow>
                {(workSpan || dayPauseMinutes !== null) && (
                    <DiaryWorkSpanHint
                        span={workSpan}
                        pauseMinutes={dayPauseMinutes}
                        currentStart={watchedStart}
                        currentEnd={watchedEnd}
                        currentPauseMinutes={breakToMinutes(watchedBreak)}
                        onApplyStart={(value) =>
                            setValue('workStart', value, {
                                shouldDirty: true
                            })
                        }
                        onApplyEnd={(value) =>
                            setValue('workEnd', value, {
                                shouldDirty: true
                            })
                        }
                        onApplyPause={(minutes) =>
                            setValue('breakTime', String(minutes), {
                                shouldDirty: true
                            })
                        }
                        entriesPath={
                            timeEntriesPath
                                ? timeEntriesPath(projectId, dayIso)
                                : undefined
                        }
                        onOpenEntries={
                            timeEntriesPath
                                ? () =>
                                      guardedNav.requestNavigate(
                                          timeEntriesPath(projectId, dayIso)
                                      )
                                : undefined
                        }
                    />
                )}
                {breakWarningId && (
                    <Alert severity="warning">
                        <FormattedMessage id={breakWarningId} />
                    </Alert>
                )}
            </FormSectionBox>

            <FormSectionBox titlePrefix="diary.section" id="weather">
                {multiSelectJoined(
                    'weatherCondition',
                    WEATHER_CONDITION_OPTIONS
                )}
                <FormRow>
                    {text('weatherTemperature')}
                    {text('weatherExtra')}
                </FormRow>
            </FormSectionBox>

            <FormSectionBox titlePrefix="diary.section" id="activities">
                {refText('topsoilRemoval', {
                    icon: <MdiIcon path={mdiExcavator} />
                })}
                {refText('surveying', {
                    icon: <MdiIcon path={mdiMapSearch} />
                })}
                {refText('cleaning', {
                    icon: <MdiIcon path={mdiBroom} />
                })}
                {refText('cuttingFindings', {
                    icon: <MdiIcon path={mdiShovel} />
                })}
                {refText('drawing', {
                    icon: <MdiIcon path={mdiBrush} />
                })}
                {refText('descriptions', {
                    icon: <MdiIcon path={mdiPencil} />
                })}
                {refText('recovery', {
                    icon: <MdiIcon path={mdiWheelBarrow} />
                })}
                {refText('finds', {
                    icon: <EmojiEventsIcon />
                })}
                {refText('photos', {
                    icon: <MdiIcon path={mdiCamera} />
                })}
            </FormSectionBox>

            <FormSectionBox titlePrefix="diary.section" id="excavator">
                {suggest('excavatorType', suggestions.excavatorType)}
                <FormRow>
                    {text('excavatorCount')}
                    {time('excavatorStart')}
                    {time('excavatorEnd')}
                    {text('excavatorBreak')}
                </FormRow>
                {refText('excavatorNote')}
            </FormSectionBox>

            <FormSectionBox titlePrefix="diary.section" id="numbers">
                {/* The single numbers in one row; the day's Sondagen with
                    their Teilfotos follow below as a list. */}
                <FormRow>
                    {numberField('lastFindingNumber', numberLinks.finding)}
                    {numberField('lastProfileNumber', numberLinks.profile)}
                    {/* EF-Nr., Geoprofil and Sondage live in tools we hold no
                        data for — nothing to link to. The Sondage is the last
                        one handed out, apart from the list below. */}
                    {numberField('lastStrayFindNumber')}
                    {numberField('lastGeoprofileNumber')}
                    {numberField('lastSondageNumber')}
                </FormRow>
                {/* Sondage and Teilfoto are a LIST: a day works several, each
                    with its own Teilfoto. Ruled off from the single numbers
                    above — a different kind of entry. */}
                <Divider />
                <DiarySondagePhotoEditor
                    value={sondagePhotos}
                    onChange={setSondagePhotos}
                />
                {numberWarnings.length > 0 && previousDay && (
                    <Alert severity="warning">
                        <FormattedMessage
                            id="diary.numbers.decreasedWarning"
                            values={{
                                date: formatDate(previousDay.recordedAt),
                                fields: numberWarnings
                                    .map((warning) =>
                                        intl.formatMessage(
                                            {
                                                id: 'diary.numbers.decreasedField'
                                            },
                                            {
                                                label: intl.formatMessage({
                                                    id: `diary.field.${warning.field}`
                                                }),
                                                value: warning.value,
                                                previous: warning.previous
                                            }
                                        )
                                    )
                                    .join(', ')
                            }}
                        />
                    </Alert>
                )}
                {photoWarnings.length > 0 && (
                    <Alert severity="warning">
                        <FormattedMessage
                            id="diary.numbers.photoDecreasedWarning"
                            values={{
                                entries: photoWarnings
                                    .map((warning) =>
                                        intl.formatMessage(
                                            {
                                                id: 'diary.numbers.photoDecreasedEntry'
                                            },
                                            {
                                                sondage: warning.sondage,
                                                value: warning.value,
                                                previous: warning.previous,
                                                date: formatDate(warning.date)
                                            }
                                        )
                                    )
                                    .join(', ')
                            }}
                        />
                    </Alert>
                )}
                {/* What the day before handed out — offered whole, since most
                    days hand out nothing new and then all of it carries over. */}
                {carryOverOffers.length > 0 && (
                    <DiaryPreviousNumbersList
                        date={carryOverDate}
                        offers={carryOverOffers}
                        missingCount={openOffers.length}
                        onTake={takeCarryOver}
                        onTakeAll={takeAllCarryOver}
                    />
                )}
            </FormSectionBox>

            {/* Only where the grids are: the field app has no cross-record
                tables to send anyone to. */}
            {projectBasePath && (
                <FormSectionBox titlePrefix="diary.section" id="records">
                    <DiaryDayRecordsList
                        counts={dayCounts}
                        basePath={projectBasePath(projectId)}
                        date={dayIso}
                        onNavigate={guardedNav.requestNavigate}
                    />
                </FormSectionBox>
            )}

            <MediaSectionBox
                projectId={projectId}
                ownerType="diary"
                ownerId={isEdit && diary ? diary.id : null}
                canEdit={canEditMedia}
                location={{ diaryDate: dayIso }}
                onNavigate={guardedNav.requestNavigate}
            />
        </RecordFormDialog>
    );
}
